import { startRegistration } from '@simplewebauthn/browser';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/components/atoms/Button';
import ErrorMessage from '@/components/atoms/ErrorMessage';
import TextFieldInput from '@/components/atoms/Form/TextFieldInput';
import { useAuthSuccess } from '@/hooks/useAuthSuccess';
import {
    useConfirmWebauthnMutation,
    useLazyMeQuery,
    useSetupWebauthnOptionsMutation,
} from '@/services/innovorder/auth/auth.endpoints';
import { getErrorCode, stringifyError } from '@/utils/errors';

import { Actions, BackupCodeItem, BackupCodesList, Subtitle, Title } from '../Mfa.style';

type Props = {
    mfaToken: string;
};

const WebAuthnTab = ({ mfaToken }: Props): ReactElement => {
    const intl = useIntl();
    const [setupOptions, { data: optionsData, error: optionsError, isLoading: optionsLoading }] =
        useSetupWebauthnOptionsMutation();
    const [confirmWebauthn, { data: confirmData, error: confirmError, isLoading: confirmLoading }] =
        useConfirmWebauthnMutation();
    const [getMe, { data: meData, error: meError }] = useLazyMeQuery();
    const { handleAuthSuccess, error: authSuccessError } = useAuthSuccess();

    const [deviceName, setDeviceName] = useState('');
    const [ceremonyError, setCeremonyError] = useState<string | null>(null);
    const [bearerToken, setBearerToken] = useState<string | null>(null);
    const [backupCodesAcknowledged, setBackupCodesAcknowledged] = useState(false);

    // The browser ceremony fires only when the user clicks Register *and* the API has
    // returned options. We trigger it from a dedicated effect so React state updates from
    // the mutation settle first (otherwise startRegistration would race with the rerender).
    useEffect(() => {
        if (!optionsData?.options) return;
        let cancelled = false;
        const run = async (): Promise<void> => {
            try {
                const attestation = await startRegistration({ optionsJSON: optionsData.options });
                if (cancelled) return;
                confirmWebauthn({
                    token: mfaToken,
                    attestation,
                    deviceName: deviceName.trim() || undefined,
                });
            } catch (err) {
                if (cancelled) return;
                // User cancelled the prompt or hit a platform error — surface generic.
                setCeremonyError(err instanceof Error ? err.message : 'webauthn_ceremony_failed');
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [optionsData, confirmWebauthn, mfaToken, deviceName]);

    const handleRegister = useCallback(() => {
        setCeremonyError(null);
        setupOptions({ token: mfaToken });
    }, [mfaToken, setupOptions]);

    const handleFinish = useCallback(() => {
        if (!confirmData?.accessToken) return;
        // 3rd-party redirect flow — same contract as the TOTP confirm: when the API echoes a
        // validated redirectUri, hand the bearer back to the originating app.
        if (confirmData.redirectUri) {
            const separator = confirmData.redirectUri.includes('?') ? '&' : '?';
            window.location.assign(`${confirmData.redirectUri}${separator}bearer=${confirmData.accessToken}`);
            return;
        }
        setBearerToken(confirmData.accessToken);
        getMe({ token: confirmData.accessToken });
        setBackupCodesAcknowledged(true);
    }, [confirmData, getMe]);

    useEffect(() => {
        if (backupCodesAcknowledged && meData && bearerToken) {
            handleAuthSuccess(meData, bearerToken);
        }
    }, [backupCodesAcknowledged, meData, bearerToken, handleAuthSuccess]);

    const confirmErrorCode = confirmError ? getErrorCode(confirmError) : null;
    const optionsErrorCode = optionsError ? getErrorCode(optionsError) : null;
    const errorLabel =
        confirmErrorCode === 'mfa_token_expired' || confirmErrorCode === 'mfa_token_required' ? (
            <FormattedMessage id="mfa.verify.tokenExpired" />
        ) : optionsErrorCode === 'mfa_token_expired' || optionsErrorCode === 'mfa_token_required' ? (
            <FormattedMessage id="mfa.verify.tokenExpired" />
        ) : authSuccessError === 'no_brands_configured' ? (
            <FormattedMessage id="login.noBrandsConfigured" />
        ) : authSuccessError === 'guest_brand_login_failed' ? (
            <FormattedMessage id="login.guestBrandLoginFailed" />
        ) : ceremonyError ? (
            <FormattedMessage id="mfa.setup.webauthn.ceremonyFailed" />
        ) : (
            stringifyError(optionsError) || stringifyError(confirmError) || stringifyError(meError)
        );

    if (confirmData && !backupCodesAcknowledged) {
        // Post-confirm: backup codes only on first MFA method. Otherwise jump straight to finalize.
        if (!confirmData.backupCodes || confirmData.backupCodes.length === 0) {
            // Fire-and-forget: handleFinish is idempotent given confirmData.
            // Calling inline would re-trigger every render; defer via effect below.
            return <FinalizeWithoutBackupCodes onMount={handleFinish} />;
        }
        return (
            <>
                <Title>
                    <FormattedMessage id="mfa.setup.backupTitle" />
                </Title>
                <Subtitle>
                    <FormattedMessage id="mfa.setup.backupSubtitle" />
                </Subtitle>
                <BackupCodesList>
                    {confirmData.backupCodes.map((backupCode) => (
                        <BackupCodeItem key={backupCode}>{backupCode}</BackupCodeItem>
                    ))}
                </BackupCodesList>
                <Actions>
                    <Button buttonType="primary" fullWidth thick onClick={handleFinish}>
                        <FormattedMessage id="mfa.setup.backupAcknowledge" />
                    </Button>
                </Actions>
            </>
        );
    }

    const inFlight = optionsLoading || confirmLoading;

    return (
        <>
            <Title>
                <FormattedMessage id="mfa.setup.webauthn.title" />
            </Title>
            <Subtitle>
                <FormattedMessage id="mfa.setup.webauthn.subtitle" />
            </Subtitle>
            <TextFieldInput
                placeholder={intl.formatMessage({ id: 'mfa.setup.webauthn.deviceNamePlaceholder' })}
                value={deviceName}
                onChange={(e): void => setDeviceName(e.currentTarget.value)}
            />
            {errorLabel ? <ErrorMessage>{errorLabel}</ErrorMessage> : null}
            <Actions>
                <Button buttonType="primary" fullWidth thick disabled={inFlight} onClick={handleRegister}>
                    <FormattedMessage id="mfa.setup.webauthn.register" />
                </Button>
            </Actions>
        </>
    );
};

// Side-effect-only component: invokes the parent's finalize once on mount.
// Used when the API returns an empty backupCodes array (additional method enrolment).
const FinalizeWithoutBackupCodes = ({ onMount }: { onMount: () => void }): ReactElement => {
    useEffect(() => {
        onMount();
    }, [onMount]);
    return (
        <Subtitle>
            <FormattedMessage id="mfa.setup.webauthn.finalizing" />
        </Subtitle>
    );
};

export default WebAuthnTab;
